import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { ArrowRight, CheckCircle2, Flag } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface WizardBannerProps {
  step: 1 | 2 | 3;
  isNextDisabled?: boolean;
  onNext?: () => void;
  nextUrl?: string;
  nextLabel?: string;
  isLastStep?: boolean;
}

export function WizardBanner({
  step,
  isNextDisabled = false,
  onNext,
  nextUrl,
  nextLabel = "Lanjutkan",
  isLastStep = false,
}: WizardBannerProps) {
  const navigate = useNavigate();

  const steps = [
    { id: 1, title: "Verifikasi Domain" },
    { id: 2, title: "Isi Knowledge" },
    { id: 3, title: "Pasang Widget" },
  ];

  const handleNext = () => {
    if (onNext) {
      onNext();
    } else if (nextUrl) {
      navigate(nextUrl);
    }
  };

  return (
    <div className="sticky top-0 z-50 w-full bg-primary/5 border-b border-primary/20 backdrop-blur-md px-4 py-3 animate-in fade-in slide-in-from-top-4 duration-500">
      <div className="max-w-[1304px] mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex flex-col gap-1 w-full md:w-auto">
          <div className="flex items-center gap-2">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
              {step}
            </span>
            <h2 className="text-sm font-semibold text-foreground">
              Langkah {step}: {steps[step - 1].title}
            </h2>
          </div>
          <div className="flex items-center gap-2 w-full md:w-64">
            <Progress value={(step / 3) * 100} className="h-1.5" />
            <span className="text-[10px] text-muted-foreground whitespace-nowrap">
              {Math.round((step / 3) * 100)}%
            </span>
          </div>
        </div>

        <div className="flex items-center gap-4 w-full md:w-auto justify-between md:justify-end">
          <div className="hidden lg:flex items-center gap-6">
            {steps.map((s) => (
              <div
                key={s.id}
                className={cn(
                  "flex items-center gap-2 text-[11px] font-medium transition-colors",
                  step === s.id ? "text-primary" : step > s.id ? "text-emerald-600" : "text-muted-foreground/50"
                )}
              >
                {step > s.id ? (
                  <CheckCircle2 className="h-3 w-3" />
                ) : (
                  <span className={cn(
                    "h-4 w-4 rounded-full border flex items-center justify-center text-[9px]",
                    step === s.id ? "border-primary text-primary" : "border-muted-foreground/30 text-muted-foreground/30"
                  )}>
                    {s.id}
                  </span>
                )}
                {s.title}
              </div>
            ))}
          </div>

          {!isLastStep && (
            <Button
              size="sm"
              onClick={handleNext}
              disabled={isNextDisabled}
              className={cn(
                "shadow-lg shadow-primary/20 transition-all active:scale-95",
                step === 3 && "bg-emerald-600 hover:bg-emerald-700"
              )}
            >
              {nextLabel}
              {step === 3 ? (
                <Flag className="ml-2 h-4 w-4" />
              ) : (
                <ArrowRight className="ml-2 h-4 w-4" />
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
